import React, { useState } from 'react';
import CollapsibleSection from '../CollapsibleSection';
import Modal from '../Modal';
import ErrorMessage from '../ErrorMessage';
import MessageModal from '../MessageModal';
import * as appointmentsApi from '../../api/appointments';
import * as opportunitiesApi from '../../api/opportunities';
import moment from 'moment';

// Utility functions
const parseLocalDateTime = (dateTimeString) => {
  if (!dateTimeString) return new Date();
  const normalized = dateTimeString.replace(' ', 'T');
  const [datePart, timePart] = normalized.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes, seconds] = (timePart || '00:00:00').split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes, seconds || 0);
};

const formatLocalDateTime = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const AppointmentsSection = ({ appointments = [], loading = false, clients = [], opportunities = [], user = null }) => {
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // Filter appointments to this week and sort chronologically
  const getThisWeeksAppointments = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate end of this week (7 days from today)
    const endOfWeek = new Date(today);
    endOfWeek.setDate(endOfWeek.getDate() + 7);

    // Filter appointments within this week
    const filtered = appointments.filter(apt => {
      // Parse date from appointment - handle both MM/DD/YYYY and ISO formats
      let aptDate;
      if (apt.date.includes('/')) {
        // MM/DD/YYYY format from toLocaleDateString
        const [month, day, year] = apt.date.split('/');
        aptDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        // ISO format or other
        aptDate = new Date(apt.date);
      }
      aptDate.setHours(0, 0, 0, 0);
      return aptDate >= today && aptDate < endOfWeek;
    });

    // Sort chronologically (earliest first)
    return filtered.sort((a, b) => {
      // Parse dates
      let aDate, bDate;
      if (a.date.includes('/')) {
        const [month, day, year] = a.date.split('/');
        aDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        aDate = new Date(a.date);
      }

      if (b.date.includes('/')) {
        const [month, day, year] = b.date.split('/');
        bDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        bDate = new Date(b.date);
      }

      // If same day, sort by time
      if (aDate.toDateString() === bDate.toDateString()) {
        const aTime = a.time || '00:00';
        const bTime = b.time || '00:00';
        return aTime.localeCompare(bTime);
      }

      // Otherwise sort by date
      return aDate - bDate;
    });
  };

  const thisWeeksAppointments = getThisWeeksAppointments();

  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentModal(true);
  };

  const AppointmentCard = ({ appointment, onEditClick }) => (
    <div
      className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 mb-1">{appointment.clientName}</h4>
          <p className="text-sm text-gray-600">
            {appointment.time} - {appointment.date}
          </p>
        </div>
      </div>

      {/* Quick action icons */}
      <div className="flex gap-2 pt-3 border-t border-gray-200">
        {appointment.phone && (
          <a
            href={`tel:${appointment.phone}`}
            onClick={(e) => e.stopPropagation()}
            className="text-blue-600 hover:text-blue-900"
            title="Call"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
          </a>
        )}
        {appointment.location && (
          <a
            href={`https://maps.google.com/?q=${appointment.location}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-blue-600 hover:text-blue-900"
            title="View on Google Maps"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </a>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEditClick(appointment);
          }}
          className="text-blue-600 hover:text-blue-900"
          title="Edit Appointment"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </button>
      </div>
    </div>
  );

  const AppointmentDetailsModal = ({ appointment, onClose }) => (
    <Modal onClose={onClose} title="Appointment Details">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Title</label>
          <p className="mt-1 text-gray-900">{appointment.title || 'Untitled'}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Client</label>
          <p className="mt-1 text-gray-900">{appointment.clientName}</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Date</label>
            <p className="mt-1 text-gray-900">{appointment.date}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Time</label>
            <p className="mt-1 text-gray-900">{appointment.time}</p>
          </div>
        </div>

        {appointment.location && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Location</label>
            <p className="mt-1 text-gray-900">{appointment.location}</p>
          </div>
        )}

        {appointment.description && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <p className="mt-1 text-gray-900">{appointment.description}</p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );

  const AppointmentEditModal = ({ appointment, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
      title: appointment?.title || '',
      description: appointment?.description || '',
      startDatetime: appointment?.start_datetime ?
        appointment.start_datetime.replace(' ', 'T').substring(0, 16) : '',
      duration: appointment?.start_datetime && appointment?.end_datetime ?
        Math.round((parseLocalDateTime(appointment.end_datetime) - parseLocalDateTime(appointment.start_datetime)) / (1000 * 60)) : 60,
      location: appointment?.location || '',
      status: appointment?.status || 'scheduled'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showEmailModal, setShowEmailModal] = useState(false);

    const selectedClient = clients.find(c => c.id === appointment?.client_id);
    const selectedOpportunity = opportunities.find(opp => opp.id === appointment?.opportunity_id);

    // Log for debugging
    console.log('AppointmentEditModal - appointment:', appointment);
    console.log('AppointmentEditModal - selectedClient:', selectedClient);

    const handleSaveEdit = async () => {
      try {
        setLoading(true);
        setError(null);

        const startDate = parseLocalDateTime(formData.startDatetime);
        const endDate = new Date(startDate.getTime() + (formData.duration * 60000));
        const newEndDatetime = formatLocalDateTime(endDate);

        const originalStart = appointment.start_datetime ? appointment.start_datetime.replace(' ', 'T').substring(0, 16) : '';
        const isRescheduled = (formData.startDatetime !== originalStart || formData.status === 'rescheduled');

        await appointmentsApi.updateAppointment(appointment.id, {
          title: formData.title,
          description: formData.description,
          startDatetime: formData.startDatetime,
          endDatetime: newEndDatetime,
          location: formData.location,
          status: formData.status
        });

        if (isRescheduled && selectedClient) {
          setShowEmailModal(true);
        } else {
          onSuccess('Appointment updated successfully!');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    return (
      <>
        <Modal onClose={onClose} title="Edit Appointment">
          <form className="space-y-4">
            {error && <ErrorMessage message={error} />}

            <div>
              <label className="block text-sm font-medium text-gray-700">Client</label>
              <p className="mt-1 text-gray-900">{selectedClient?.name || appointment?.clientName || 'Unknown Client'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Title *</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Date & Time *</label>
                <input
                  type="datetime-local"
                  required
                  value={formData.startDatetime}
                  onChange={(e) => setFormData({...formData, startDatetime: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Duration *</label>
                <select
                  value={formData.duration}
                  onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                  <option value={150}>2.5 hours</option>
                  <option value={180}>3 hours</option>
                  <option value={240}>4 hours</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Location</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="scheduled">Scheduled</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="rescheduled">Rescheduled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={loading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Modal>

        {showEmailModal && selectedClient && (
          <MessageModal
            isOpen={showEmailModal}
            onClose={() => {
              setShowEmailModal(false);
              onSuccess('Appointment updated and email sent successfully!');
            }}
            templateType="appointment_rescheduled"
            recipientEmail={selectedClient.email || ''}
            contextData={{
              client_name: selectedClient.name || '',
              appointment_title: formData.title,
              appointment_date: moment(formData.startDatetime).format('MMMM D, YYYY'),
              appointment_time: moment(formData.startDatetime).format('h:mm A'),
              appointment_location: formData.location || '',
              ...(selectedOpportunity?.project_name && { project_name: selectedOpportunity.project_name }),
              company_name: user?.companyName || user?.username || ''
            }}
            opportunityId={appointment?.opportunity_id || null}
            onSuccess={() => {
              setShowEmailModal(false);
              onSuccess('Appointment updated and email sent successfully!');
            }}
          />
        )}
      </>
    );
  };

  const loadingCards = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[1, 2].map((i) => (
        <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-4 animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-2/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/3"></div>
        </div>
      ))}
    </div>
  );

  const emptyState = (
    <div className="text-center py-8">
      <p className="text-gray-500">No appointments this week</p>
    </div>
  );

  const appointmentsList = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {thisWeeksAppointments.map((apt) => (
        <AppointmentCard
          key={apt.id}
          appointment={apt}
          onEditClick={(appointment) => {
            setSelectedAppointment(appointment);
            setShowEditModal(true);
          }}
        />
      ))}
    </div>
  );

  return (
    <>
      <CollapsibleSection
        title="Appointments"
        icon={
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        }
        badge={loading ? '...' : thisWeeksAppointments.length}
        defaultOpen={true}
      >
        {loading ? loadingCards : thisWeeksAppointments.length === 0 ? emptyState : appointmentsList}
      </CollapsibleSection>

      {/* Appointment Details Modal */}
      {showAppointmentModal && selectedAppointment && (
        <AppointmentDetailsModal
          appointment={selectedAppointment}
          onClose={() => {
            setShowAppointmentModal(false);
            setSelectedAppointment(null);
          }}
        />
      )}

      {/* Appointment Edit Modal */}
      {showEditModal && selectedAppointment && (
        <AppointmentEditModal
          appointment={selectedAppointment}
          onClose={() => {
            setShowEditModal(false);
            setSelectedAppointment(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedAppointment(null);
            window.location.reload();
          }}
        />
      )}
    </>
  );
};

export default AppointmentsSection;
